// Users admin view: a clickable user list with full CRUD. A row click opens a
// right-side DETAIL Drawer with role/status toggles (PATCH) and Delete (DELETE,
// confirm). Adding a user lives behind a "+ New user" MODAL. The public
// self-registration flag (Settings) stays here. Admin-gated like before.

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useAuth } from '../auth/AuthContext';
import { Card, Loading, ErrorState, Badge, StatusBadge } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { Modal, ConfirmDialog } from '../components/Modal';
import { Drawer, DetailRow } from '../components/Drawer';
import { OneTimePasswordDialog, formatUntil } from '../components/OneTimePasswordDialog';
import { friendly } from './Networks';
import type { User } from '../api/schemas';

type UserRole = User['role'];

export function Users() {
  const { isAdmin, user: me } = useAuth();
  const users = useResource(() => api.users(), []);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<User | null>(null);
  // EV1 — can the gateway send mail (reset-dialog default, «отправить письмо ещё раз»)?
  const [mailAvailable, setMailAvailable] = useState(false);
  useEffect(() => {
    let active = true;
    api.authMethods().then((m) => active && setMailAvailable(m.email)).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!isAdmin) {
    return (
      <div className="view">
        <div className="view-head"><h1>Users</h1></div>
        <Card><div className="empty">Admin access required.</div></Card>
      </div>
    );
  }

  const cols: Column<User>[] = [
    { key: 'email', header: 'Email', cell: (u) => <b>{u.email}</b> },
    { key: 'name', header: 'Name', cell: (u) => u.name ?? '—' },
    { key: 'role', header: 'Role', cell: (u) => <Badge tone={u.role === 'admin' ? 'info' : 'muted'}>{u.role}</Badge> },
    { key: 'status', header: 'Status', cell: (u) => <StatusBadge status={u.status} /> },
    { key: 'created', header: 'Created', cell: (u) => (u.created_at ? new Date(u.created_at).toLocaleString() : '—') },
  ];

  return (
    <div className="view">
      <div className="view-head">
        <h1>Users</h1>
        <div className="view-head-actions">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New user</button>
        </div>
      </div>

      <RegistrationSettings />

      <Card title="Users">
        {users.loading ? (
          <Loading what="Loading users…" />
        ) : users.error || !users.data ? (
          <ErrorState message={users.error ?? 'no data'} onRetry={users.reload} />
        ) : (
          <DataTable columns={cols} rows={users.data.items} rowKey={(u) => u.id} empty="No users." onRowClick={(u) => setSelected(u)} />
        )}
      </Card>

      {creating && (
        <AddUserModal
          onClose={() => setCreating(false)}
          onAdded={() => {
            setCreating(false);
            users.reload();
          }}
        />
      )}

      {selected && (
        <UserDrawer
          user={selected}
          // OTP1 — the one-time reset is an admin act on SOMEONE ELSE (the gateway refuses self).
          canResetPassword={isAdmin && selected.id !== me?.id}
          mailAvailable={mailAvailable}
          onClose={() => setSelected(null)}
          onChanged={(next) => {
            users.reload();
            setSelected(next);
          }}
        />
      )}
    </div>
  );
}

function UserDrawer({
  user,
  canResetPassword = false,
  mailAvailable = false,
  onClose,
  onChanged,
}: {
  user: User;
  canResetPassword?: boolean;
  mailAvailable?: boolean;
  onClose: () => void;
  onChanged: (next: User | null) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function patch(kind: 'role' | 'status') {
    setBusy(kind);
    setErr(null);
    try {
      const next = kind === 'role'
        ? await api.patchUser(user.id, { role: user.role === 'admin' ? 'user' : 'admin' })
        : await api.patchUser(user.id, { status: user.status === 'suspended' ? 'active' : 'suspended' });
      onChanged(next);
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(null);
    }
  }

  const [mailNote, setMailNote] = useState<string | null>(null);

  // EV1 — «отметить подтверждённым» / «отправить письмо ещё раз» for an unconfirmed address.
  async function markVerified() {
    setBusy('verify');
    setErr(null);
    try {
      onChanged(await api.markEmailVerified(user.id));
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(null);
    }
  }

  async function resendVerification() {
    setBusy('resend');
    setErr(null);
    setMailNote(null);
    try {
      await api.sendEmailVerification(user.id);
      setMailNote(`Письмо со ссылкой подтверждения отправлено на ${user.email}.`);
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(null);
    }
  }

  async function del() {
    setBusy('delete');
    setErr(null);
    try {
      await api.deleteUser(user.id);
      onChanged(null);
      onClose();
    } catch (e) {
      setErr(friendly(e));
      setBusy(null);
    }
  }

  return (
    <Drawer
      title={user.email}
      subtitle={user.name ?? undefined}
      onClose={onClose}
      actions={
        <>
          <button className="btn btn-sm" disabled={busy != null} onClick={() => patch('role')}>
            {user.role === 'admin' ? 'Make user' : 'Make admin'}
          </button>
          <button className="btn btn-sm btn-danger" disabled={busy != null} onClick={() => patch('status')}>
            {user.status === 'suspended' ? 'Enable' : 'Disable'}
          </button>
          {canResetPassword && (
            <button className="btn btn-sm" data-testid="otp-open" disabled={busy != null} onClick={() => setResetting(true)}>
              Сбросить пароль (одноразовый)
            </button>
          )}
          <button className="btn btn-sm btn-danger" disabled={busy != null} onClick={() => setConfirmDel(true)}>Delete</button>
        </>
      }
    >
      {err && <ErrorState message={err} />}
      <DetailRow label="Role"><Badge tone={user.role === 'admin' ? 'info' : 'muted'}>{user.role}</Badge></DetailRow>
      <DetailRow label="Status"><StatusBadge status={user.status} /></DetailRow>
      <DetailRow label="E-mail">
        <span data-testid="email-verified-badge">
          {user.email_verified !== false ? (
            <Badge tone="ok">подтверждён</Badge>
          ) : (
            <Badge tone="warn">не подтверждён</Badge>
          )}
        </span>
        {user.email_verified !== false && user.email_verified_at && (
          <span className="muted small"> {new Date(user.email_verified_at).toLocaleString()}</span>
        )}
        {user.email_verified === false && (
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
            <button className="btn btn-sm" data-testid="email-mark-verified" disabled={busy != null} onClick={() => void markVerified()}>
              Отметить подтверждённым
            </button>
            {mailAvailable && (
              <button className="btn btn-sm" data-testid="email-resend" disabled={busy != null} onClick={() => void resendVerification()}>
                Отправить письмо ещё раз
              </button>
            )}
          </span>
        )}
        {mailNote && <div className="setting-help" data-testid="email-resend-note">{mailNote}</div>}
      </DetailRow>
      <DetailRow label="User id"><code className="mono small">{user.id}</code></DetailRow>
      <DetailRow label="Org"><code className="mono small">{user.org_id}</code></DetailRow>
      {user.created_at && <DetailRow label="Created">{new Date(user.created_at).toLocaleString()}</DetailRow>}
      {user.one_time_password_expires_at && (
        <DetailRow label="Одноразовый пароль">
          <span data-testid="otp-pending">выдан, действует до {formatUntil(user.one_time_password_expires_at)}</span>
        </DetailRow>
      )}

      {resetting && (
        <OneTimePasswordDialog
          user={user}
          mailAvailable={mailAvailable}
          onClose={() => setResetting(false)}
          // Keep the drawer open on the same user, now showing the pending expiry; the list reloads.
          onDone={(res) => onChanged({ ...user, one_time_password_expires_at: res.expires_at })}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          title="Delete user"
          message={<>Delete <b>{user.email}</b>? They lose access immediately.</>}
          confirmLabel="Delete user"
          busy={busy === 'delete'}
          error={err}
          onConfirm={del}
          onClose={() => setConfirmDel(false)}
        />
      )}
    </Drawer>
  );
}

// Admin control for the public self-registration flag (GET/PATCH /settings).
function RegistrationSettings() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .getSettings()
      .then((s) => active && setEnabled(s.registration_enabled))
      .catch((e) => active && setErr(friendly(e)));
    return () => {
      active = false;
    };
  }, []);

  async function toggle() {
    if (enabled === null) return;
    setBusy(true);
    setErr(null);
    try {
      const next = await api.updateSettings({ registration_enabled: !enabled });
      setEnabled(next.registration_enabled);
    } catch (e) {
      setErr(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Settings">
      {err && <div className="form-error">{err}</div>}
      <div className="setting-row">
        <div className="setting-text">
          <div className="setting-label">Allow public registration</div>
          <div className="setting-help">When on, anyone can create an account from the public sign-up page.</div>
        </div>
        <label className="switch">
          <input type="checkbox" checked={enabled ?? false} disabled={enabled === null || busy} onChange={toggle} />
          <span className="switch-track" />
        </label>
      </div>
    </Card>
  );
}

function AddUserModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createUser({
        email: email.trim(),
        role,
        display_name: displayName.trim() || null,
        password: password ? password : null,
      });
      onAdded();
    } catch (ex) {
      setErr(friendly(ex));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add user"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-sm btn-primary" disabled={busy || !email.trim()} onClick={submit}>{busy ? 'Adding…' : 'Add user'}</button>
        </>
      }
    >
      <form onSubmit={submit} className="issue-form">
        <label className="field">Email
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@org.dev" />
        </label>
        <label className="field">Display name
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="optional" />
        </label>
        <label className="field">Role
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
        </label>
        <label className="field">Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="leave empty for SSO-only" />
        </label>
        {err && <div className="form-error">{err}</div>}
      </form>
    </Modal>
  );
}
